import React, { useEffect, useRef, useState } from 'react';
import { Users, BookOpen, Award as AwardIcon, Rocket } from 'lucide-react';
import './WhyChooseUs.css';

const milestones = [
  {
    icon: Users,
    title: 'Expert Guidance',
    description: 'Learn from industry professionals who understand your journey.',
  },
  {
    icon: BookOpen,
    title: 'Hands-On Learning',
    description: 'Real-world projects that build practical expertise.',
  },
  {
    icon: AwardIcon,
    title: 'Recognized Certification',
    description: 'Credentials that open doors and validate your skills.',
  },
  {
    icon: Rocket,
    title: 'Career Acceleration',
    description: 'Tools and support to advance faster and with confidence.',
  },
];

export function WhyChooseUs() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % milestones.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section className="why-choose-us section" ref={sectionRef}>
      <div className="why-header">
        <h2 className="section-title">Your Growth, Our Mission</h2>
        <p className="section-subtitle">
          A proven framework designed to support every step of your professional evolution.
        </p>
      </div>

      <div className="timeline-container">
        <div className="timeline-line">
          <div 
            className="timeline-progress"
            style={{
              width: isVisible ? `${((activeIndex + 1) / milestones.length) * 100}%` : '0%',
            }}
          ></div>
        </div>

        <div className="milestones">
          {milestones.map((milestone, index) => {
            const Icon = milestone.icon;
            return (
              <div
                key={index}
                className={`milestone ${index <= activeIndex ? 'active' : ''} ${
                  index === activeIndex ? 'current' : ''
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="milestone-icon">
                  <Icon size={28} />
                </div>
                <h3 className="milestone-title">{milestone.title}</h3>
                <p className="milestone-description">{milestone.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
